import { z } from 'zod';

// ─── Persona Event Payloads ─────────────────────────────────────────
// Zod schemas for every persona-domain event.
// Consumers validate incoming payloads against these at the boundary.

/** Upper bound on personas materialized per Discover invocation (guards a runaway mock/BQ pull). */
export const PERSONA_DISCOVER_MAX_BATCH = 50;

/**
 * Avatar generation attempts allowed per persona before it stops being requeued.
 * Image generation is billed per call, so a persona that keeps failing must not
 * re-enter the queue on every sync forever.
 */
export const PERSONA_AVATAR_MAX_GENERATION_ATTEMPTS = 3;

export const PersonaDiscoverRequestedPayload = z.object({
    tenantId: z.string().min(1),
    /** Nexus user id of the caller — optional; used for audit/analytics only. */
    requestedBy: z.string().optional(),
    /** How many not-yet-added personas (by spend desc) to materialize this run. */
    batchSize: z.number().int().positive().max(PERSONA_DISCOVER_MAX_BATCH).optional(),
});
export type PersonaDiscoverRequestedPayload = z.infer<typeof PersonaDiscoverRequestedPayload>;

/**
 * Published by the upstream DS analysis pipeline when a Tenant's regenerated
 * personalities are ready in BigQuery — closes the personas feedback loop
 * (re-sync + mark rejects processed).
 *
 * DS process key is `session_id` (same UUID as RejectedPersona.onboardingSessionId /
 * ONBOARDING_COMPLETED.onboardingSessionId). Nexus resolves tenantId from those
 * rows and stamps processedAt only for that session. `tenantId` alone remains a
 * compat path that marks all unprocessed rejects for the tenant.
 *
 * `correlationId` here is the upstream pipeline's own tracing id, distinct from
 * the Pub/Sub envelope's; the handler falls back to the envelope's when omitted.
 */
export const PersonaFeedbackReadyPayload = z
    .object({
        /** DS onboarding-batch process id — preferred close-the-loop key. */
        session_id: z.string().uuid().optional(),
        /** Compat: mark all unprocessed rejects for the tenant when session_id is omitted. */
        tenantId: z.string().min(1).optional(),
        correlationId: z.string().optional(),
    })
    .refine((p) => Boolean(p.session_id || p.tenantId), {
        message: 'Either session_id or tenantId is required',
    });
export type PersonaFeedbackReadyPayload = z.infer<typeof PersonaFeedbackReadyPayload>;

/**
 * Published by Nexus when the user confirms "Go to my personas" so adge_backend /
 * DS can recalculate core personalities from the tenant's unprocessed rejects.
 */
export const PersonaOnboardingCompletedPayload = z.object({
    tenantId: z.string().min(1),
    /**
     * BQ client identifier(s) for the retag pipeline — the same `@userName` the
     * personas BQ queries use (`tenant_settings.customerPartition`, falling back
     * to `defaultBrand`). Comma-separated when the tenant maps to several clients.
     */
    clientName: z.string().min(1),
    /** Tenant main industry (BQ slug) from tenant_settings.defaultIndustry. */
    industry: z.string(),
    correlationId: z.string().optional(),
    /** Client-minted review batch id — DS uses this to retag this batch's rejects. */
    onboardingSessionId: z.string().uuid(),
});
export type PersonaOnboardingCompletedPayload = z.infer<typeof PersonaOnboardingCompletedPayload>;

/**
 * Requests avatar generation (A2) for one persona already persisted in Postgres.
 * Published after create/upsert (data-sync, Discover) whenever `avatarUploadId` is
 * still null — never republished once an avatar exists (no regenerate-on-refresh).
 */
export const PersonaAvatarGenerationRequestedPayload = z.object({
    tenantId: z.string().min(1),
    personaId: z.string().min(1),
});
export type PersonaAvatarGenerationRequestedPayload = z.infer<typeof PersonaAvatarGenerationRequestedPayload>;
